import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';

interface AddressSuggestion {
  address: string;
  id: string;
}

interface AddressLookupProps {
  onAddressSelect: (address: string) => void;
}

interface AddressDetails {
  line_1: string;
  line_2: string;
  line_3: string;
  line_4: string;
  town_or_city: string;
  county: string;
  postcode: string;
}

export function AddressLookup({ onAddressSelect }: AddressLookupProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualEntry, setManualEntry] = useState(false);
  const [manualAddress, setManualAddress] = useState<AddressDetails>({
    line_1: '',
    line_2: '',
    line_3: '',
    line_4: '',
    town_or_city: '',
    county: '',
    postcode: ''
  });

  const handleManualSubmit = () => {
    const formattedAddress = [
      manualAddress.line_1,
      manualAddress.line_2,
      manualAddress.line_3,
      manualAddress.line_4,
      manualAddress.town_or_city,
      manualAddress.county,
      manualAddress.postcode
    ]
      .filter(Boolean)
      .join(', ');

    onAddressSelect(formattedAddress);
    setManualEntry(false);
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.length < 3 || manualEntry) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response = await fetch(
          `https://api.getAddress.io/autocomplete/${encodeURIComponent(searchTerm)}?api-key=rkPeAT1JTEa5a6pJDxQ1zA45005`,
          {
            headers: {
              'Accept': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch address suggestions');
        }

        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } catch (err) {
        setError('Error fetching address suggestions');
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, manualEntry]);

  const handleAddressSelect = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `https://api.getAddress.io/get/${id}?api-key=rkPeAT1JTEa5a6pJDxQ1zA45005`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch address details');
      }

      const details: AddressDetails = await response.json();
      const formattedAddress = [
        details.line_1,
        details.line_2,
        details.line_3,
        details.line_4,
        details.town_or_city,
        details.county,
        details.postcode
      ]
        .filter(Boolean)
        .join(', ');

      onAddressSelect(formattedAddress);
      setSearchTerm('');
      setSuggestions([]);
    } catch (err) {
      setError('Error fetching address details');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative w-full">
      {manualEntry ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
            <input
              type="text"
              value={manualAddress.line_1}
              onChange={(e) => setManualAddress(prev => ({ ...prev, line_1: e.target.value }))}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
            <input
              type="text"
              value={manualAddress.line_2}
              onChange={(e) => setManualAddress(prev => ({ ...prev, line_2: e.target.value }))}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Town/City</label>
            <input
              type="text"
              value={manualAddress.town_or_city}
              onChange={(e) => setManualAddress(prev => ({ ...prev, town_or_city: e.target.value }))}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
            <input
              type="text"
              value={manualAddress.county}
              onChange={(e) => setManualAddress(prev => ({ ...prev, county: e.target.value }))}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input
              type="text"
              value={manualAddress.postcode}
              onChange={(e) => setManualAddress(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setManualEntry(false)}
              className="text-gray-600 hover:text-gray-900"
            >
              Back to Search
            </button>
            <button
              type="button"
              onClick={handleManualSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Save Address
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Start typing your address..."
              className="w-full p-2 pr-10 border rounded"
              aria-label="Search address"
            />
            <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          <button
            type="button"
            onClick={() => setManualEntry(true)}
            className="mt-2 text-sm text-indigo-600 hover:text-indigo-700"
          >
            Enter address manually
          </button>
        </>
      )}

      {isLoading && (
        <div className="absolute w-full bg-white border rounded-b mt-1 p-2 text-gray-500">
          Loading...
        </div>
      )}

      {error && (
        <div className="absolute w-full bg-red-50 border border-red-200 rounded-b mt-1 p-2 text-red-600">
          {error}
        </div>
      )}

      {suggestions.length > 0 && (
        <ul className="absolute w-full bg-white border rounded-b mt-1 shadow-lg max-h-60 overflow-auto z-10">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.id}
              onClick={() => handleAddressSelect(suggestion.id)}
              className="p-2 hover:bg-gray-50 cursor-pointer text-sm"
            >
              {suggestion.address}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}